import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import { FaDiceD20 } from 'react-icons/fa';

import { Button, Grid, Text, Popover, Avatar } from "@geist-ui/core";
import { BookOpen, Book, Map, Archive, User, Clock, LogOut, Navigation } from "@geist-ui/icons";
import { trackNavigation } from '../../config/analytics';
import { useAuth } from '../../context/AuthContext';

const READER_GLOBAL_STATE_KEY = 'om_play_global_reader_state';

const Navbar = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout, isPatron } = useAuth();

	// Handle scroll effect
	useEffect(() => {
		const handleScroll = () => {
			const isScrolled = window.scrollY > 20;
			setScrolled(isScrolled);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	// Close menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isOpen && !event.target.closest('.nav-menu') && !event.target.closest('.hamburger-menu')) {
			setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	// Close menu when route changes
	useEffect(() => {
		setIsOpen(false);
	}, [location]);

	const toggleMenu = () => {
		setIsOpen(!isOpen);
	};

	const handleNavigation = (path, isExternal) => {
		if (isExternal) {
			window.open(path, '_blank', 'noopener,noreferrer');
		} else if (path === "/story") {
			try {
				// First try to get the last book the user was reading
				const lastBookStr = localStorage.getItem('lastBook');
				if (lastBookStr) {
					const lastBook = JSON.parse(lastBookStr);
					if (lastBook && lastBook.id) {
						trackNavigation(location.pathname, `/story/${lastBook.id}`);
						navigate(`/story/${lastBook.id}`, { state: lastBook });
						setIsOpen(false);
						return;
					}
				}
				
				// Fall back to global reader state if no last book
				const globalReaderState = localStorage.getItem(READER_GLOBAL_STATE_KEY);
				if (globalReaderState) {
					const state = JSON.parse(globalReaderState);
					if (state.bookId) {
						trackNavigation(location.pathname, `/story/${state.bookId}`);
						navigate(`/story/${state.bookId}`);
						setIsOpen(false);
						return;
					}
					}
				
				// Default to wolves_of_langston if no saved state
				trackNavigation(location.pathname, `/story/wolves_of_langston`);
				navigate(`/story/wolves_of_langston`);
			} catch (error) {
				console.error("Error loading saved book state:", error);
				navigate(`/story/wolves_of_langston`);
			}
		} else {
			trackNavigation(location.pathname, path);
			navigate(path);
		}
		setIsOpen(false);
	};

	// Split nav items into primary and secondary groups
	const primaryNavItems = [
		{ path: "/story", label: "Story", icon: <BookOpen /> },
		{ path: "/table", label: "Table", icon: <Map /> },
		{ path: "/travel", label: "Travel", icon: <Navigation /> }, // Using Navigation icon
	];

	const secondaryNavItems = [
		{ path: "/bookshelf", label: "Bookshelf", icon: <Book /> },
		{
			path: "https://obviousmimic.com/pages/solo-adventure-rules",
			label: "How to Play",
			icon: <FaDiceD20 />,
			external: true
		}
	];

	const handleLogout = () => {
		logout();
		trackNavigation(location.pathname, '/');
	};

	return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="navbar-container">
        <div
          className={`hamburger-menu ${isOpen ? "open" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          role="button"
          tabIndex={0}
        >
          <div className="hamburger-line"></div>
          <div className="hamburger-line"></div>
          <div className="hamburger-line"></div>
        </div>

        <Link to="/" className="navbar-logo">
          <div className="logo-container">
            <img src="https://pub-3cbdf0fe4d9c40f19241eb1a5a52005c.r2.dev/om-assets/chest.webp" alt="Chester icon" className="chester-icon" />
            <span>ObviousMimic.AI</span>
          </div>
        </Link>

        <div className={`nav-menu ${isOpen ? "open" : ""}`}>
          <Grid.Container gap={1.5}>
            {/* Render primary nav items */}
            {primaryNavItems.map(item => (
              <Grid key={item.path}>
                <Button
                  icon={item.icon}
                  onClick={() => item.external ? handleNavigation(item.path, true) : handleNavigation(item.path, false)}
                  aria-label={item.label}
				  auto                  
				  className="theme-button theme-button--w100"
                  type={`${
                    location.pathname === item.path ? "secondary-light" : ""
                  }`}
                >
                  <Text span>{item.label}</Text>
                </Button>
              </Grid>
            ))}
            
            {/* Add divider */}
            <Grid>
              <div className="navbar-divider">
                <div className="navbar-divider-line"></div>
              </div>
            </Grid>
            
            {/* Render secondary nav items */}
            {secondaryNavItems.map(item => (
              <Grid key={item.path}>
                <Button
                  icon={item.icon}
                  onClick={() => item.external ? handleNavigation(item.path, true) : handleNavigation(item.path, false)}
                  aria-label={item.label}
				  auto                  
				  className="theme-button theme-button--w100"
                  type={`${
                    location.pathname === item.path ? "secondary-light" : ""
                  }`}
                >
                  <Text span>{item.label}</Text>
                </Button>
              </Grid>
            ))}
          </Grid.Container>
        </div>

        {user && (
          <div className="user-profile-nav">
            <Popover
              content={
                <div className="user-popover">
                  <div className="user-popover-header">
                    <Text h4>{user.fullName || 'User'}</Text>
                    <Text p type="secondary">{user.email || ''}</Text>
                    {user.isPatron && (
                      <div className="patron-badge">
                        <Text span className="patron-text">
                          {user.tier || 'Patron'}
                        </Text>
                      </div>
                    )}
                    {user.is_forced === true && (
                      <div className="patron-badge" style={{ backgroundColor: '#0070f3' }}>
                        <Text span className="patron-text">
                          Special Access
                        </Text>
                      </div>
                    )}
                  </div>
                  <div className="user-popover-actions">
                    <Button 
                      icon={<LogOut />} 
                      onClick={handleLogout}
                      auto
                      type="secondary"
                      className="logout-button"
                      size="small"
                    >
                      <Text span>Logout</Text>
                    </Button>
                  </div>
                </div>
              }
              placement="bottomEnd"
              portalClassName="user-profile-popover"
            >
              <div className="user-avatar-container">
                <Avatar 
                  src={user.avatar || undefined} 
                  text={user.fullName?.charAt(0)?.toUpperCase() || 'U'} 
                  className="user-avatar"
                  size="small"
                />
              </div>
            </Popover>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;